import {
    CharacterEntity,
    db,
    ProjectEntity,
    ScreenplayEntity,
    ShotListEntity,
    StoryEntity,
    StoryboardEntity,
    SyncMetadataEntity,
} from '@/shared/lib/db'
import { collection, getDocs, QueryDocumentSnapshot } from 'firebase/firestore'

import { firestore } from './firebase'

type FirestoreRecord = Record<string, any>

function normalizeFirestoreValue<T>(value: T): T {
    if (Array.isArray(value)) {
        return value.map((item) => normalizeFirestoreValue(item)) as T
    }

    if (value && typeof value === 'object') {
        if ('toDate' in (value as Record<string, unknown>) && typeof (value as any).toDate === 'function') {
            return (value as any).toDate() as T
        }

        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).map(([key, nestedValue]) => [
                key,
                normalizeFirestoreValue(nestedValue),
            ])
        ) as T
    }

    return value
}

function mapDoc<T extends FirestoreRecord>(
    snapshot: QueryDocumentSnapshot,
    transform?: (data: FirestoreRecord, id: string) => T
): T {
    const raw = normalizeFirestoreValue(snapshot.data() as FirestoreRecord)
    const mapped = transform
        ? transform(raw, snapshot.id)
        : ({ id: snapshot.id, ...raw } as unknown as T)
    return mapped
}

async function getCollectionDocs<T extends FirestoreRecord>(
    pathSegments: string[],
    transform?: (data: FirestoreRecord, id: string) => T
) {
    const snapshot = await getDocs(collection(firestore, pathSegments.join('/')))
    return snapshot.docs.map((docSnapshot) => mapDoc<T>(docSnapshot, transform))
}

async function upsertSyncMetadata(metadata: SyncMetadataEntity) {
    await db.sync_metadata.put(metadata)
}

export async function hydrateUserData(userId: string) {
    await upsertSyncMetadata({
        userId,
        status: 'syncing',
        error: undefined,
        lastHydratedAt: undefined,
    })

    try {
        const projects = await getCollectionDocs<ProjectEntity>(['users', userId, 'projects'], (data, id) => ({
            id,
            title: data.title,
            created_at: data.created_at,
            updated_at: data.updated_at,
            genre: data.genre,
            subGenre: data.subGenre ?? null,
            userId: data.userId ?? userId,
        }))

        const screenplays: ScreenplayEntity[] = []
        const stories: StoryEntity[] = []
        const characters: CharacterEntity[] = []
        const shotlists: ShotListEntity[] = []
        const storyboards: StoryboardEntity[] = []

        for (const project of projects) {
            const projectPath = ['users', userId, 'projects', project.id]

            const [
                projectScreenplays,
                projectStories,
                projectCharacters,
                projectShotlists,
                projectStoryboards,
            ] = await Promise.all([
                getCollectionDocs<ScreenplayEntity>([...projectPath, 'screenplays'], (data, id) => ({
                    id,
                    content: data.content ?? [],
                    title: data.title ?? id,
                    projectId: data.projectId ?? project.id,
                    userId: data.userId ?? userId,
                    created_at: data.created_at,
                    updated_at: data.updated_at,
                })),
                getCollectionDocs<StoryEntity>([...projectPath, 'stories'], (data, id) => ({
                    id,
                    content: data.content ?? [],
                    title: data.title ?? id,
                    projectId: data.projectId ?? project.id,
                    userId: data.userId ?? userId,
                    created_at: data.created_at,
                    updated_at: data.updated_at,
                })),
                getCollectionDocs<CharacterEntity>([...projectPath, 'characters'], (data, id) => ({
                    ...(data as CharacterEntity),
                    id,
                    projectId: data.projectId ?? project.id,
                })),
                getCollectionDocs<ShotListEntity>([...projectPath, 'shotlists'], (data, id) => ({
                    id,
                    projectId: data.projectId ?? project.id,
                    sceneId: data.sceneId,
                    shots: data.shots ?? [],
                    created_at: data.created_at,
                    updated_at: data.updated_at,
                })),
                getCollectionDocs<StoryboardEntity>([...projectPath, 'storyboards'], (data, id) => ({
                    id,
                    projectId: data.projectId ?? project.id,
                    sceneId: data.sceneId,
                    frames: data.frames ?? [],
                    created_at: data.created_at,
                    updated_at: data.updated_at,
                })),
            ])

            screenplays.push(...projectScreenplays)
            stories.push(...projectStories)
            characters.push(...projectCharacters)
            shotlists.push(...projectShotlists)
            storyboards.push(...projectStoryboards)
        }

        await db.transaction(
            'rw',
            [db.project, db.screenplays, db.stories, db.characters, db.shotlists, db.storyboards, db.sync_metadata],
            async () => {
                await db.project.bulkPut(projects)
                await db.screenplays.bulkPut(screenplays)
                await db.stories.bulkPut(stories)
                await db.characters.bulkPut(characters)
                await db.shotlists.bulkPut(shotlists)
                await db.storyboards.bulkPut(storyboards)
                await upsertSyncMetadata({
                    userId,
                    status: 'synced',
                    lastHydratedAt: new Date(),
                    error: undefined,
                })
            }
        )
    } catch (error) {
        console.error('Failed to hydrate user data', error)
        await upsertSyncMetadata({
            userId,
            status: 'error',
            lastHydratedAt: undefined,
            error: error instanceof Error ? error.message : 'Unknown sync error',
        })
        throw error
    }
}

export async function clearLocalData() {
    await Promise.all(db.tables.map((table) => table.clear()))
}
